import * as Sentry from '@sentry/react';
import { User } from '../types/RoleType';
import { isProduction, isStaging, isDevelopment, EnvironmentConfig } from './environment';

/**
 * Sentry Configuration and Utilities
 *
 * Provides error monitoring and performance tracking for production.
 * Must be initialized in main.tsx before React renders.
 */

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;
const SENTRY_ENVIRONMENT = import.meta.env.VITE_SENTRY_ENVIRONMENT || 'development';
const SENTRY_RELEASE = import.meta.env.VITE_SENTRY_RELEASE || 'unknown';

/**
 * Initialize Sentry
 * Call this in main.tsx before ReactDOM.createRoot()
 */
export function initSentry(): void {
  // Only initialize if DSN is provided
  if (!SENTRY_DSN) {
    console.warn('Sentry DSN not found. Error monitoring is disabled.');
    return;
  }

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: SENTRY_ENVIRONMENT,
    release: SENTRY_RELEASE,
    // Only enable in staging and production to avoid polluting Sentry with dev noise
    enabled: isStaging() || isProduction(),

    // Performance Monitoring
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],

    // Performance sample rate
    // 1.0 = 100% of transactions for performance monitoring
    // Adjust based on traffic volume to control costs
    tracesSampleRate: EnvironmentConfig.sentryTracesSampleRate(),

    // Session Replay sample rate
    // For errors: always capture replay
    // For sessions: sample rate (lower in production to reduce costs)
    replaysSessionSampleRate: EnvironmentConfig.sentryReplaySessionSampleRate(),
    replaysOnErrorSampleRate: 1.0,
    sendDefaultPii: true,
    // Ignore certain errors
    ignoreErrors: [
      // Network errors
      'NetworkError',
      'Failed to fetch',
      'Network request failed',
      'Load failed',

      // Auth redirects (not actual errors)
      'Redirecting to login',
      'Unauthorized',

      // Browser extension errors
      'Extension context invalidated',

      // ResizeObserver errors (not critical)
      'ResizeObserver loop limit exceeded',
    ],

    // Filter out breadcrumbs for sensitive data
    beforeBreadcrumb(breadcrumb) {
      // Don't log console breadcrumbs in production
      if (breadcrumb.category === 'console' && isProduction()) {
        return null;
      }

      // Filter out sensitive URLs
      if (breadcrumb.data?.url) {
        const url = breadcrumb.data.url;
        if (url.includes('password') || url.includes('token')) {
          breadcrumb.data.url = '[FILTERED]';
        }
      }

      return breadcrumb;
    },

    // Modify events before sending
    beforeSend(event) {
      // Filter out PII from event data
      if (event.request?.cookies) {
        delete event.request.cookies;
      }

      if (event.request?.headers) {
        delete event.request.headers['Authorization'];
        delete event.request.headers['Cookie'];
      }

      return event;
    },
  });

  if (isDevelopment()) {
    console.warn(`Sentry initialized in ${SENTRY_ENVIRONMENT} environment`);
  }
}

/**
 * Set user context for Sentry
 * Call this after successful login
 */
export function setSentryUser(user: User): void {
  if (!SENTRY_DSN) return;

  Sentry.setUser({
    id: user.id,
    email: user.email,
    username: `${user.firstName} ${user.lastName}`,
    segment: user.roleType,
  });

  // Set additional context
  Sentry.setContext('user_details', {
    roleType: user.roleType,
    roles: user.roles,
    instituteName: user.instituteName,
  });
}

/**
 * Clear user context for Sentry
 * Call this on logout
 */
export function clearSentryUser(): void {
  if (!SENTRY_DSN) return;

  Sentry.setUser(null);
}

/**
 * Manually capture an exception
 * Use for caught errors that should still be reported
 */
export function captureException(error: Error, context?: Record<string, any>, tags?: Record<string, string>): void {
  if (!SENTRY_DSN) {
    console.error('Sentry not initialized. Error:', error);
    return;
  }

  Sentry.captureException(error, {
    contexts: context ? { custom: context } : undefined,
    tags,
  });
}

/**
 * Manually capture a message
 * Use for important logs that should be tracked
 */
export function captureMessage(
  message: string,
  level: Sentry.SeverityLevel = 'info',
  context?: Record<string, any>,
  tags?: Record<string, string>
): void {
  if (!SENTRY_DSN) {
    console.log(`[${level}] ${message}`, context);
    return;
  }

  Sentry.captureMessage(message, {
    level,
    contexts: context ? { custom: context } : undefined,
    tags,
  });
}

/**
 * Add a breadcrumb
 * Use to track user actions for debugging
 */
export function addBreadcrumb(
  message: string,
  category: string,
  level: Sentry.SeverityLevel = 'info',
  data?: Record<string, any>
): void {
  if (!SENTRY_DSN) return;

  Sentry.addBreadcrumb({
    message,
    category,
    level,
    data,
    timestamp: Date.now() / 1000,
  });
}

/**
 * Start a performance span
 * @deprecated Use Sentry.startSpan() for new code (startTransaction removed in Sentry v8)
 */
export function startTransaction(): undefined {
  return undefined;
}

/**
 * Check if Sentry is enabled
 */
export function isSentryEnabled(): boolean {
  return !!SENTRY_DSN;
}

/**
 * Register global window-level error handlers.
 * Call once in main.tsx after initSentry().
 *
 * Captures:
 * - Uncaught synchronous JS errors (window.onerror)
 * - Unhandled Promise rejections (unhandledrejection)
 *
 * These errors occur outside React's component tree (e.g. async code,
 * third-party scripts, setTimeout callbacks) and would otherwise be lost.
 */
export function registerGlobalErrorHandlers(): void {
  window.onerror = (message, source, lineno, colno, error) => {
    if (error) {
      captureException(error, {
        source: String(source ?? 'unknown'),
        lineno: String(lineno ?? ''),
        colno: String(colno ?? ''),
      });
    } else {
      captureMessage(String(message), 'error', {
        source: String(source ?? 'unknown'),
      });
    }

    // Belt-and-suspenders: also send to App Insights and flush immediately.
    // The App Insights SDK auto-chains onto window.onerror, but that chain can
    // break if another library overwrites window.onerror after us. Explicit
    // tracking here guarantees the error always reaches App Insights.
    import('./appInsights').then(({ trackAppException, trackAppTrace, flushAppInsights, SeverityLevel }) => {
      if (error) {
        trackAppException(error, {
          source: String(source ?? 'unknown'),
          lineno: String(lineno ?? ''),
          colno: String(colno ?? ''),
          origin: 'window.onerror',
        }, SeverityLevel.Critical);
      } else {
        trackAppTrace(
          String(message),
          SeverityLevel.Error,
          { source: String(source ?? 'unknown'), origin: 'window.onerror' }
        );
      }
      flushAppInsights();
    });

    // Return false to preserve default browser error behaviour (console display)
    return false;
  };

  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    const error =
      event.reason instanceof Error
        ? event.reason
        : new Error(String(event.reason ?? 'Unhandled promise rejection'));

    captureException(error, {
      type: 'unhandledrejection',
      promiseReason: String(event.reason ?? ''),
    });

    // Also send to App Insights — unhandledrejection has its own listener in the
    // App Insights SDK, but explicit tracking ensures it is always captured even
    // if the SDK listener is registered after this one.
    import('./appInsights').then(({ trackAppException, flushAppInsights, SeverityLevel }) => {
      trackAppException(error, {
        origin: 'unhandledrejection',
        promiseReason: String(event.reason ?? ''),
      }, SeverityLevel.Critical);
      flushAppInsights();
    });
  });
}
